'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Scenario {
  id: string;
  title: string;
  content: string;
  description: string;
}

interface LessonData {
  moduleId: number;
  lessonId: string;
  lessonTitle: string;
  topic: string;
  scenarioType: 'preloaded' | 'hybrid' | 'freeform';
  scenarios?: Scenario[];
}

interface Message {
  role: 'user' | 'anna';
  content: string;
  timestamp?: string;
}

interface PracticeWithAnnaProps {
  lessonData: LessonData;
  isPaidUser: boolean;
}

type ComponentState =
  | 'INITIAL' |'SCENARIO_LOADED' |'LOADING' |'CONVERSATION' |'SESSION_CLOSE' |'ERROR';

// ─── Suggestion cards per lesson ──────────────────────────────────────────────

const SUGGESTION_CARDS: Record<string, string[]> = {
  '1-1': [
    "What's the difference between Copilot and ChatGPT?",
    'Where do I find Copilot in Microsoft 365?',
    "Can I use Copilot if my company hasn't switched it on yet?",
    'Is Copilot free or do I need a subscription?',
  ],
  '1-2': [
    'Summarise this email thread I\'ll paste — what are the decisions and actions?',
    'Help me draft a response to a long email I received. Tone: professional.',
    'I have 20 unread emails. Help me decide which ones actually need my attention.',
    "What's the fastest way to summarise a meeting email thread?",
  ],
  '1-3': [
    'I joined the meeting late. Can you summarise what I missed?',
    'Generate meeting notes from this Teams recording.',
    'What questions should I ask to look informed in this meeting?',
  ],
  '1-4': [
    'Write a first draft of a status report for my leadership team.',
    "I've written this draft. Make it tighter without changing my voice.",
    'Turn this outline into a one-page proposal.',
  ],
  '1-5': [
    "I'm looking at this data. What should I notice? What questions should I ask?",
    'Help me spot the trend in this spreadsheet.',
    'What does this data tell me about my business question?',
  ],
  '1-6': [
    'Give me 3 prompts I could use for emails.',
    "What's a prompt I could use to summarise a long document?",
    "I'm stuck. What could I ask Copilot right now?",
  ],
};

// ─── Module 1 preloaded scenarios ─────────────────────────────────────────────

const MODULE1_SCENARIOS: Record<string, Scenario[]> = {
  '1-2': [
    {
      id: 'scenario-1',
      title: 'Budget Review Email',
      description: 'A leadership email about Q3 budget cuts with decisions and actions',
      content: `From: Sarah Chen <sarah.chen@company.com>
Date: June 18, 2026
Subject: Q3 Budget Review — Decisions Needed

Hi Team,

Following up on the budget review meeting on Friday. We've made the following decisions and need actions from everyone.

DECISIONS:
- Marketing budget will be reduced 8% effective October 1
- We will not hire additional headcount this quarter
- All discretionary spending paused until Finance review (July 1)
- Final numbers confirmed by Finance by end of week

ACTIONS NEEDED:
- Marketing: Submit revised budget forecast by Thursday 5pm
- All department heads: Review your headcount plan and confirm by Friday
- Finance: Circulate updated project codes by Wednesday

Let me know if you have questions. I'll be in the office all week.

Best,
Sarah`,
    },
    {
      id: 'scenario-2',
      title: 'Meeting Recap Email',
      description: 'A post-meeting summary with decisions and next steps',
      content: `From: James Rodriguez <j.rodriguez@company.com>
Date: June 17, 2026
Subject: Product Roadmap Review — Next Steps

Team,

Great discussion in today's meeting. Here's what we agreed:

PRODUCT ROADMAP DECISIONS:
- Phase 2 launch moves from August 15 to September 15 (2-week slip for additional testing)
- We will NOT include the custom reporting feature in Phase 2 (moving to Phase 3)
- Customer communications team will draft new timeline email for review by Thursday

WHO'S DOING WHAT:
- Marcus (PM): Update project plan and Jira by EOD today
- Priya (Design): Finalize UI mockups for Phase 2 by next Tuesday
- QA Lead: Confirm testing scope for extended timeline by Friday
- Product: Brief support team on Phase 2 scope changes by next Monday

Let me know if you need clarification. I'll send the updated roadmap doc separately.

James`,
    },
    {
      id: 'scenario-3',
      title: 'Project Status with Issues',
      description: 'A status update that surfaces problems needing attention',
      content: `From: Devon Lee <d.lee@company.com>
Date: June 19, 2026
Subject: Platform Rebuild — Status Update + Risk Alert

Hi Leadership,

Status update on the platform rebuild project. Progress is good overall, but I'm flagging one risk that needs escalation.

PROGRESS THIS MONTH:
✓ Database migration 75% complete
✓ API layer refactored (on schedule)
✓ Frontend components tested with new system
✓ Internal demo scheduled for June 28

UPCOMING MILESTONES:
- Full internal testing begins June 23
- Customer beta launch target: July 15
- Full production cutover: August 1

⚠️ RISK ALERT: Third-party payment processor integration is showing a 2-week lead time delay. If not resolved by June 25, we will miss the July 15 beta launch window. We've identified an alternative vendor, but switching now costs 1 week of dev time.

DECISION NEEDED: Can we schedule a brief call this week to discuss the processor risk? I need sign-off on either: (1) negotiate faster timeline with current vendor, or (2) switch to backup vendor.

Thanks,
Devon`,
    },
  ],
};

// ─── Helper: get Supabase auth token ─────────────────────────────────────────

async function getAuthToken(): Promise<string | null> {
  const supabase = createClient();
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function PracticeWithAnna({ lessonData, isPaidUser }: PracticeWithAnnaProps) {
  const [componentState, setComponentState] = useState<ComponentState>('INITIAL');
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [exchangeCount, setExchangeCount] = useState(0);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [lastUserMessage, setLastUserMessage] = useState('');
  const [lastScenarioContent, setLastScenarioContent] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Get scenarios for this lesson
  const scenarios = lessonData.scenarios || MODULE1_SCENARIOS[lessonData.lessonId] || [];
  const suggestionCards = SUGGESTION_CARDS[lessonData.lessonId] || [];

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const handleScenarioSelect = (scenario: Scenario) => {
    setSelectedScenario(scenario);
    setComponentState('SCENARIO_LOADED');
  };

  const handleCardClick = (cardText: string) => {
    setInputValue(cardText);
    setTimeout(() => {
      inputRef.current?.focus();
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
      }
    }, 50);
  };

  const handleSend = useCallback(async () => {
    const message = inputValue.trim();
    if (!message || componentState === 'LOADING') return;
    if (message.length < 3) return;

    const userMsg: Message = {
      role: 'user',
      content: message,
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setComponentState('LOADING');
    setLastUserMessage(message);

    const scenarioContent = selectedScenario?.content || lastScenarioContent;
    setLastScenarioContent(scenarioContent);

    try {
      const token = await getAuthToken();
      if (!token) {
        setErrorMessage('Please log in to practice with Anna.');
        setComponentState('ERROR');
        return;
      }

      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
      const response = await fetch(`${supabaseUrl}/functions/v1/practice-with-anna`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userMessage: message,
          scenarioContent: scenarioContent || undefined,
          lessonContext: {
            moduleId: lessonData.moduleId,
            lessonId: lessonData.lessonId,
            lessonTitle: lessonData.lessonTitle,
            topic: lessonData.topic,
            scenarioType: lessonData.scenarioType,
            scenarioId: selectedScenario?.id || null,
          },
          isPaidUser,
          sessionId,
        }),
      });

      const data = await response.json();

      if (!data.success && !data.blocked) {
        setErrorMessage(data.error || "Something didn't work. Try again?");
        setComponentState('ERROR');
        return;
      }

      const annaMsg: Message = {
        role: 'anna',
        content: data.annaMessage,
      };

      setMessages((prev) => [...prev, annaMsg]);
      setExchangeCount(data.exchangeCount || exchangeCount + 1);

      if (data.sessionId) setSessionId(data.sessionId);

      if (data.sessionClosing) {
        setComponentState('SESSION_CLOSE');
      } else {
        setComponentState('CONVERSATION');
      }
    } catch {
      setErrorMessage("Something didn't work. Try again?");
      setComponentState('ERROR');
    }
  }, [inputValue, componentState, selectedScenario, lastScenarioContent, sessionId, lessonData, isPaidUser, exchangeCount]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleRetry = () => {
    setInputValue(lastUserMessage);
    setMessages((prev) => prev.slice(0, -1)); // Remove last user message if it was added
    setComponentState(messages.length > 0 ? 'CONVERSATION' : selectedScenario ? 'SCENARIO_LOADED' : 'INITIAL');
    setErrorMessage('');
  };

  const handleNewSession = () => {
    setMessages([]);
    setExchangeCount(0);
    setSessionId(null);
    setSelectedScenario(null);
    setInputValue('');
    setComponentState('INITIAL');
    setLastScenarioContent('');
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ border: '1px solid rgba(27,42,74,0.12)', backgroundColor: '#FAFAF8' }}
      role="region"
      aria-label="Practice With Anna"
    >
      {/* Header */}
      <div
        className="px-6 py-4 flex items-center gap-3"
        style={{ backgroundColor: 'var(--navy)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'var(--amber)' }}
          aria-hidden="true"
        >
          <span className="font-body text-xs font-bold text-white">A</span>
        </div>
        <div>
          <p className="font-body text-sm font-semibold text-white">Practice With Anna</p>
          <p className="font-body text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
            {lessonData.lessonTitle}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="p-6">

        {/* ── INITIAL: Scenario selector ── */}
        {componentState === 'INITIAL' && scenarios.length > 0 && (
          <div>
            <p
              className="font-body text-sm font-semibold mb-4"
              style={{ color: 'var(--navy)' }}
            >
              Pick an email to practice with:
            </p>
            <div className="flex flex-col gap-3 mb-5">
              {scenarios.map((scenario) => (
                <button
                  key={scenario.id}
                  onClick={() => handleScenarioSelect(scenario)}
                  className="text-left rounded-xl p-4 transition-all duration-150 hover:shadow-md active:scale-[0.99]"
                  style={{
                    backgroundColor: '#F9F7F4',
                    border: '1px solid rgba(27,42,74,0.1)',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.01)';
                    (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(27,42,74,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
                    (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
                  }}
                >
                  <p className="font-body text-sm font-semibold mb-1" style={{ color: 'var(--navy)' }}>
                    {scenario.title}
                  </p>
                  <p className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>
                    {scenario.description}
                  </p>
                </button>
              ))}
            </div>
            <p className="font-body text-xs italic" style={{ color: 'var(--text-muted)' }}>
              Once you pick a scenario, you'll see the email and can practice a prompt on it.
            </p>
          </div>
        )}

        {/* ── INITIAL: No scenarios (suggestion cards only) ── */}
        {componentState === 'INITIAL' && scenarios.length === 0 && (
          <div>
            <p className="font-body text-sm font-semibold mb-4" style={{ color: 'var(--navy)' }}>
              Try one of these prompts:
            </p>
            <SuggestionCards cards={suggestionCards} onCardClick={handleCardClick} />
            <InputArea
              inputRef={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onSend={handleSend}
             
              placeholder="What would you like to practice?"
            />
          </div>
        )}

        {/* ── SCENARIO LOADED ── */}
        {componentState === 'SCENARIO_LOADED' && selectedScenario && (
          <div>
            {/* Scenario label */}
            <p
              className="font-body text-xs uppercase tracking-widest font-semibold mb-2"
              style={{ color: 'var(--amber)', letterSpacing: '0.1em' }}
            >
              Scenario: {selectedScenario.title}
            </p>

            {/* Email content box */}
            <div
              className="rounded-xl p-5 mb-5 overflow-auto"
              style={{
                backgroundColor: '#F0EDE8',
                border: '1px solid rgba(27,42,74,0.1)',
                maxHeight: '220px',
              }}
            >
              <pre
                className="font-body text-xs leading-relaxed whitespace-pre-wrap"
                style={{ color: '#374151', fontFamily: 'inherit' }}
              >
                {selectedScenario.content}
              </pre>
            </div>

            {/* Suggestion cards */}
            <p className="font-body text-sm font-semibold mb-3" style={{ color: 'var(--navy)' }}>
              Now try one of these prompts:
            </p>
            <SuggestionCards cards={suggestionCards} onCardClick={handleCardClick} />

            <p className="font-body text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
              Or write your own prompt below:
            </p>
            <InputArea
              inputRef={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onSend={handleSend}
             
              placeholder="What would you like to practice?"
            />
          </div>
        )}

        {/* ── CONVERSATION ── */}
        {(componentState === 'CONVERSATION' || componentState === 'LOADING') && (
          <div>
            {/* Scenario reference (collapsed) */}
            {selectedScenario && (
              <div
                className="rounded-lg px-4 py-2 mb-4 flex items-center justify-between cursor-pointer"
                style={{ backgroundColor: '#F0EDE8', border: '1px solid rgba(27,42,74,0.08)' }}
                onClick={() => setComponentState('SCENARIO_LOADED')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setComponentState('SCENARIO_LOADED')}
                aria-label="View scenario content"
              >
                <p className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>
                  <span className="font-semibold" style={{ color: 'var(--navy)' }}>Scenario: </span>
                  {selectedScenario.title}
                </p>
                <span className="font-body text-xs" style={{ color: 'var(--amber)' }}>View ↑</span>
              </div>
            )}

            {/* Message thread */}
            <div
              className="rounded-xl overflow-y-auto mb-4"
              style={{ maxHeight: '320px', minHeight: '120px' }}
              role="log"
              aria-live="polite"
              aria-label="Conversation with Anna"
            >
              <div className="flex flex-col gap-3 p-1">
                {messages.map((msg, i) => (
                  <MessageBubble key={i} message={msg} />
                ))}
                {componentState === 'LOADING' && <LoadingBubble />}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Exchange counter */}
            <p className="font-body text-xs mb-3 text-right" style={{ color: 'var(--text-muted)' }}>
              {exchangeCount} of 6 exchanges
            </p>

            {/* Suggestion cards (only if no messages yet or early in conversation) */}
            {messages.length <= 1 && suggestionCards.length > 0 && (
              <div className="mb-3">
                <SuggestionCards cards={suggestionCards} onCardClick={handleCardClick} compact />
              </div>
            )}

            <InputArea
              inputRef={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onSend={handleSend}
              disabled={componentState === 'LOADING'}
              placeholder="Your turn — what would you try?"
            />
          </div>
        )}

        {/* ── SESSION CLOSE ── */}
        {componentState === 'SESSION_CLOSE' && (
          <div>
            {/* Final message thread */}
            <div
              className="rounded-xl overflow-y-auto mb-4"
              style={{ maxHeight: '320px', minHeight: '80px' }}
              role="log"
              aria-label="Conversation with Anna"
            >
              <div className="flex flex-col gap-3 p-1">
                {messages.map((msg, i) => (
                  <MessageBubble key={i} message={msg} />
                ))}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Disabled input */}
            <div
              className="rounded-xl px-4 py-3 mb-4"
              style={{
                backgroundColor: '#F0EDE8',
                border: '1px solid rgba(27,42,74,0.08)',
                opacity: 0.6,
              }}
            >
              <p className="font-body text-xs italic" style={{ color: 'var(--text-muted)' }}>
                Session complete — 6 exchanges used
              </p>
            </div>

            {/* Soft conversion link for free users */}
            {!isPaidUser && (
              <p className="font-body text-xs text-center mb-4" style={{ color: 'var(--text-muted)' }}>
                Curious what's in the rest of the course?{' '}
                <a
                  href="/conversion-page"
                  className="underline underline-offset-2"
                  style={{ color: 'var(--navy)' }}
                >
                  Take a look.
                </a>
              </p>
            )}

            {/* New session button */}
            <div className="flex justify-center">
              <button
                onClick={handleNewSession}
                className="font-body text-sm font-semibold px-5 py-2.5 rounded-xl transition-all duration-150 active:scale-95"
                style={{
                  backgroundColor: 'var(--navy)',
                  color: 'white',
                }}
              >
                Start a new session
              </button>
            </div>
          </div>
        )}

        {/* ── ERROR ── */}
        {componentState === 'ERROR' && (
          <div className="text-center py-6">
            <p className="font-body text-sm mb-4" style={{ color: 'var(--navy)' }}>
              {errorMessage || "Something didn't work. Try again?"}
            </p>
            <button
              onClick={handleRetry}
              className="font-body text-sm font-semibold px-5 py-2.5 rounded-xl transition-all duration-150 active:scale-95"
              style={{ backgroundColor: 'var(--amber)', color: 'white' }}
            >
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SuggestionCards({
  cards,
  onCardClick,
  compact = false,
}: {
  cards: string[];
  onCardClick: (text: string) => void;
  compact?: boolean;
}) {
  return (
    <div className={`flex flex-wrap gap-2 ${compact ? 'mb-2' : 'mb-4'}`}>
      {cards.map((card, i) => (
        <button
          key={i}
          onClick={() => onCardClick(card)}
          className="text-left rounded-lg px-3 py-2 font-body text-xs transition-all duration-150 active:scale-[0.98]"
          style={{
            backgroundColor: '#F9F7F4',
            color: 'var(--navy)',
            border: '1px solid rgba(27,42,74,0.12)',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 8px rgba(27,42,74,0.12)';
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.02)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
          }}
        >
          {card}
        </button>
      ))}
    </div>
  );
}

function InputArea({
  inputRef,
  value,
  onChange,
  onKeyDown,
  onSend,
  disabled,
  placeholder,
}: {
  inputRef: React.RefObject<HTMLTextAreaElement>;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  disabled: boolean;
  placeholder: string;
}) {
  return (
    <div
      className="flex gap-2 items-end rounded-xl p-2"
      style={{ border: '1px solid rgba(27,42,74,0.15)', backgroundColor: 'white' }}
    >
      <textarea
        ref={inputRef}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        rows={1}
        className="flex-1 resize-none font-body text-sm leading-relaxed bg-transparent outline-none px-2 py-1.5"
        style={{
          color: 'var(--navy)',
          minHeight: '36px',
          maxHeight: '120px',
          opacity: disabled ? 0.5 : 1,
        }}
        aria-label="Message input"
      />
      <button
        onClick={onSend}
        disabled={disabled || !value.trim()}
        className="flex-shrink-0 rounded-lg px-4 py-2 font-body text-sm font-semibold transition-all duration-150 active:scale-95"
        style={{
          backgroundColor: disabled || !value.trim() ? 'rgba(212,137,26,0.4)' : 'var(--amber)',
          color: 'white',
          cursor: disabled || !value.trim() ? 'not-allowed' : 'pointer',
        }}
        aria-label="Send message"
      >
        {disabled ? (
          <span className="flex items-center gap-1.5">
            <LoadingDots />
          </span>
        ) : (
          'Send'
        )}
      </button>
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mr-2 mt-0.5"
          style={{ backgroundColor: 'var(--amber)' }}
          aria-hidden="true"
        >
          <span className="font-body text-xs font-bold text-white" style={{ fontSize: '9px' }}>A</span>
        </div>
      )}
      <div
        className="rounded-2xl px-4 py-3 font-body text-sm leading-relaxed"
        style={{
          maxWidth: '80%',
          backgroundColor: isUser ? 'var(--navy)' : '#F0EDE8',
          color: isUser ? 'white' : 'var(--navy)',
          borderBottomRightRadius: isUser ? '4px' : '16px',
          borderBottomLeftRadius: isUser ? '16px' : '4px',
          whiteSpace: 'pre-wrap',
          lineHeight: '1.7',
        }}
      >
        {message.content}
      </div>
    </div>
  );
}

function LoadingBubble() {
  return (
    <div className="flex justify-start">
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mr-2 mt-0.5"
        style={{ backgroundColor: 'var(--amber)' }}
        aria-hidden="true"
      >
        <span className="font-body text-xs font-bold text-white" style={{ fontSize: '9px' }}>A</span>
      </div>
      <div
        className="rounded-2xl px-4 py-3 flex items-center gap-1"
        style={{
          backgroundColor: '#F0EDE8',
          borderBottomLeftRadius: '4px',
        }}
        aria-label="Anna is thinking"
      >
        <LoadingDots />
        <span className="font-body text-xs ml-1" style={{ color: 'var(--text-muted)' }}>
          Anna is thinking...
        </span>
      </div>
    </div>
  );
}

function LoadingDots() {
  return (
    <span className="flex gap-1" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full"
          style={{
            backgroundColor: 'var(--amber)',
            animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
          }}
        />
      ))}
      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
    </span>
  );
}
